/**
 * Gateway Command
 * CLI commands for the cross-device session gateway.
 * Allows the local CLI to serve as a relay for remote web/mobile clients.
 */

import { Command } from 'commander';
import chalk from 'chalk';
import { writeFileSync, readFileSync, existsSync, unlinkSync } from 'fs';
import { join } from 'path';
import { isAuthenticated, getConfigDir } from '../utils/config.js';
import { extractErrorMessage } from '../agent/service.js';

function getGatewayPidFile(): string {
    return join(getConfigDir(), 'gateway.pid');
}

function requireAuth(): void {
    if (!isAuthenticated()) {
        console.error(chalk.red('Authentication required. Run `paean login` first.'));
        process.exit(1);
    }
}

function writeGatewayPid(): void {
    try {
        writeFileSync(getGatewayPidFile(), JSON.stringify({
            pid: process.pid,
            startedAt: new Date().toISOString(),
            workspace: process.cwd(),
        }, null, 2));
    } catch { /* best effort */ }
}

function removeGatewayPid(): void {
    try { unlinkSync(getGatewayPidFile()); } catch { /* ignore */ }
}

function readGatewayPid(): { pid: number; startedAt: string; workspace?: string } | null {
    try {
        const pidFile = getGatewayPidFile();
        if (!existsSync(pidFile)) return null;
        return JSON.parse(readFileSync(pidFile, 'utf-8'));
    } catch { return null; }
}

function isProcessRunning(pid: number): boolean {
    try { process.kill(pid, 0); return true; } catch { return false; }
}

export const gatewayCommand = new Command('gateway')
    .description('Cross-device session gateway — relay remote requests through local CLI');

// Subcommand: gateway start
gatewayCommand
    .command('start')
    .description('Start interactive session with gateway relay (local + remote)')
    .option('-i, --interval <ms>', 'Poll interval in milliseconds', '3000')
    .option('-d, --debug', 'Enable debug logging')
    .option('--no-mcp', 'Disable local MCP server integration')
    .option('-r, --resume <conversationId>', 'Resume a previous conversation session')
    .action(async (options) => {
        console.log(chalk.yellow('\n  [Deprecated] `paean gateway start` will be removed in a future release.'));
        console.log(chalk.yellow('  Use `paean --gateway` to enable the gateway within the interactive CLI.\n'));

        requireAuth();

        const existingPid = readGatewayPid();
        if (existingPid && isProcessRunning(existingPid.pid)) {
            console.log(chalk.yellow(`Gateway already running (PID: ${existingPid.pid})`));
            console.log(chalk.gray(`Started: ${existingPid.startedAt}`));
            return;
        }

        const pollInterval = Math.max(1000, parseInt(options.interval, 10) || 3000);
        const debug = !!options.debug;
        const enableMcp = options.mcp !== false;

        writeGatewayPid();

        const { initializeAgent } = await import('../agent/utils.js');
        const { mcpState, onMcpToolCall, mcpClient } = await initializeAgent({
            mcp: enableMcp,
            debug,
        });

        let cleanedUp = false;
        const gracefulCleanup = async (signal: string) => {
            if (cleanedUp) return;
            cleanedUp = true;
            if (debug) {
                console.error(chalk.dim(`\n  Received ${signal}, cleaning up...`));
            }
            removeGatewayPid();
            try {
                await mcpClient?.disconnectAll();
            } catch { /* best-effort */ }
            process.exit(signal === 'SIGTERM' ? 143 : 130);
        };

        process.on('SIGINT', () => gracefulCleanup('SIGINT'));
        process.on('SIGTERM', () => gracefulCleanup('SIGTERM'));

        try {
            const { startChat } = await import('../agent/chat.ink.js');
            await startChat({
                mcpState,
                onMcpToolCall,
                mcpClient,
                debug,
                resumeConversationId: options.resume,
                gatewayEnabled: true,
                gatewayPollInterval: pollInterval,
            });
        } catch (error) {
            console.error(chalk.red(`Gateway failed to start: ${extractErrorMessage(error)}`));
            process.exit(1);
        } finally {
            removeGatewayPid();
            if (!cleanedUp) {
                cleanedUp = true;
                try { await mcpClient?.disconnectAll(); } catch { /* ignore */ }
            }
        }
    });

// Subcommand: gateway status
gatewayCommand
    .command('status')
    .description('Check gateway status')
    .action(() => {
        const pidInfo = readGatewayPid();
        if (!pidInfo) {
            console.log(chalk.gray('No gateway running.'));
            return;
        }

        if (!isProcessRunning(pidInfo.pid)) {
            console.log(chalk.yellow('Gateway process not found (stale PID file).'));
            removeGatewayPid();
            return;
        }

        console.log(chalk.green.bold('Gateway is running'));
        console.log(chalk.gray(`  PID: ${pidInfo.pid}`));
        console.log(chalk.gray(`  Started: ${pidInfo.startedAt}`));
        if (pidInfo.workspace) {
            console.log(chalk.gray(`  Workspace: ${pidInfo.workspace}`));
        }
    });

// Subcommand: gateway stop
gatewayCommand
    .command('stop')
    .description('Stop the running gateway')
    .action(() => {
        const pidInfo = readGatewayPid();
        if (!pidInfo) {
            console.log(chalk.gray('No gateway running.'));
            return;
        }

        if (!isProcessRunning(pidInfo.pid)) {
            console.log(chalk.yellow('Gateway process not found (stale PID file).'));
            removeGatewayPid();
            return;
        }

        try {
            process.kill(pidInfo.pid, 'SIGTERM');
            console.log(chalk.green(`Sent stop signal to gateway (PID: ${pidInfo.pid})`));
            removeGatewayPid();
        } catch (error) {
            console.error(chalk.red(`Failed to stop gateway: ${extractErrorMessage(error)}`));
        }
    });
